import { captureException } from '@sentry/react';
import Router from 'next/router';
import type { ExecutionResultWithData } from '@soundxyz/graphql-react-query';
import { gql } from '@soundxyz/graphql-react-query';
import { exhaustiveGuard } from '@soundxyz/utils';
import { createEnum, enumChecker } from '@soundxyz/utils/const';
import { styled } from '@components-shared/src/stitches';
import type { ChartTabsType } from '@components/Charts/ChartTabs';
import { TabContent, Tabs } from '@components/main-components/tabs/Tab';
import type {
  TimePeriodAggEnum,
  TopChartTimePeriodEnum,
  TopCuratorsSortEnum,
  TrendingArtistInfoQuery,
  TrendingArtistsSortEnum,
  TrendingCollectorsQuery,
  TrendingCollectorsSortEnum,
  TrendingReleasesQuery,
  TrendingReleasesSortEnum,
} from '@graphql/reactQuery';
import { NAV_LINKS } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query trendingReleases($timePeriod: TimePeriodAggEnum!, $sort: TrendingReleasesSortEnum!) {
    topReleases(timePeriod: $timePeriod, sort: $sort) {
      totalSales
      totalSalesUsd
      nftsSold
      uniqueCollectors
      release {
        id
        title
        webappUri
        staticCoverImage {
          id
          url
        }
        coverImage {
          id
          url
        }
        artist {
          id
          name
          webappUri
        }
      }
    }
  }

  query trendingArtistInfo($input: TopArtistsInput!) {
    topArtistsV2(input: $input) {
      totalSales
      totalSalesUsd
      uniqueCollectors
      nftsSold
      artist {
        id
        soundHandle
        name
        webappUri
        user {
          id
          avatar {
            id
            url
          }
          publicAddress
          twitterHandle
          username
        }
      }
    }
  }

  query trendingCollectors(
    $timePeriod: TopChartTimePeriodEnum!
    $sort: TrendingCollectorsSortEnum!
  ) {
    topCollectorsV2(timePeriod: $timePeriod, sort: $sort) {
      nftsBought
      totalSpent
      creatorsSupported
      user {
        id
        publicAddress
        twitterHandle
        username
        avatar {
          id
          url
        }
        artist {
          id
          name
          webappUri
          soundHandle
        }
        webappUri
      }
    }
  }

  query trendingCurators($input: TopAffiliateCuratorsInput!) {
    topAffiliateCurators(input: $input) {
      soundsReferred
      mintsQuantity
      totalVolume

      user {
        id
        publicAddress
        twitterHandle
        username
        avatar {
          id
          url
        }
        artist {
          id
          name
          webappUri
          soundHandle
        }
        webappUri
      }
    }
  }
`);

export const Timespan = {
  WEEK: '7d',
  MONTH: '30d',
  ALL: 'all',
} as const;

export type Timespan = (typeof Timespan)[keyof typeof Timespan];

export const isTimespan = enumChecker(Timespan);

export const TimespanValues = Object.values(Timespan);

export const TimespanToTimePeriodAggEnum = {
  all: 'ALL_TIME',
  '30d': 'ONE_MONTH',
  '7d': 'SEVEN_DAY',
} as const satisfies Record<Timespan, TimePeriodAggEnum>;

export const TimespanToTopChartPeriodEnum = {
  all: 'ALL_TIME',
  '30d': 'THIRTY_DAY',
  '7d': 'SEVEN_DAY',
} as const satisfies Record<Timespan, TopChartTimePeriodEnum>;

export interface TopReleasesProps {
  topReleases: ExecutionResultWithData<TrendingReleasesQuery>;
  timespan: Timespan;
  sort: TrendingReleasesSortEnum;
}

export interface TopArtistsProps {
  topArtists: ExecutionResultWithData<TrendingArtistInfoQuery>;
}

export interface TopCollectorsProps {
  topCollectors: ExecutionResultWithData<TrendingCollectorsQuery>;
}

export const StyledTabs = styled(Tabs, {
  width: '100%',
});

export const Panel = styled(TabContent, {
  display: 'flex',
  flexDirection: 'column',
  marginTop: 18,
});

export const TopChartsTypeValues = ['sounds', 'artists', 'collectors', 'curators'] as const;

export const TopChartsType = createEnum(TopChartsTypeValues);

export type TopChartsType = (typeof TopChartsType)[keyof typeof TopChartsType];

export const isTopChartsType = enumChecker(TopChartsType);

export const defaultTimespan = Timespan['WEEK'];

export const TOP_CHARTS_REVALIDATION =
  // 2 and a half minutes
  150;

export default function EmptyPage() {
  return null;
}

export const TopReleasesSortSubpaths = createEnum(['mints', 'total-volume', 'unique-collectors']);
export type TopReleasesSortSubpaths =
  (typeof TopReleasesSortSubpaths)[keyof typeof TopReleasesSortSubpaths];

export const isTopReleasesSortSubpaths = enumChecker(TopReleasesSortSubpaths);

export const TopReleasesSortSubpathsToSortEnum = {
  mints: 'NFTS_SOLD',
  'total-volume': 'TOTAL_SALES',
  'unique-collectors': 'UNIQUE_COLLECTORS',
} as const satisfies Record<TopReleasesSortSubpaths, TrendingReleasesSortEnum>;

export const TopArtistsSortSubpaths = createEnum(['mints', 'total-volume', 'unique-collectors']);
export type TopArtistsSortSubpaths =
  (typeof TopArtistsSortSubpaths)[keyof typeof TopArtistsSortSubpaths];

export const isTopArtistsSortSubpaths = enumChecker(TopArtistsSortSubpaths);

export const TopArtistsSortSubpathsToSortEnum = {
  mints: 'NFTS_SOLD',
  'total-volume': 'TOTAL_SALES',
  'unique-collectors': 'UNIQUE_COLLECTORS',
} as const satisfies Record<TopArtistsSortSubpaths, TrendingArtistsSortEnum>;

export const TopCollectorsSortSubpaths = createEnum(['mints', 'total-spent', 'artists-supported']);
export type TopCollectorsSortSubpaths =
  (typeof TopCollectorsSortSubpaths)[keyof typeof TopCollectorsSortSubpaths];

export const isTopCollectorsSortSubpaths = enumChecker(TopCollectorsSortSubpaths);

export const TopCollectorsSortSubpathsToSortEnum = {
  mints: 'NFTS_BOUGHT',
  'total-spent': 'TOTAL_SPENT',
  'artists-supported': 'CREATORS_SUPPORTED',
} as const satisfies Record<TopCollectorsSortSubpaths, TrendingCollectorsSortEnum>;

export const TopCuratorsSortSubpaths = createEnum(['mints', 'total-volume', 'sounds-referred']);
export type TopCuratorsSortSubpaths =
  (typeof TopCuratorsSortSubpaths)[keyof typeof TopCuratorsSortSubpaths];

export const isTopCuratorsSortSubpaths = enumChecker(TopCuratorsSortSubpaths);

export const TopCuratorsSortSubpathsToSortEnum = {
  mints: 'MINTS',
  'sounds-referred': 'SOUNDS_REFERRED',
  'total-volume': 'TOTAL_VOLUME',
} as const satisfies Record<TopCuratorsSortSubpaths, TopCuratorsSortEnum>;

function ReleasesTopChartsPathname({
  timespan,
  sort,
}: {
  timespan: Timespan;
  sort: TopReleasesSortSubpaths;
}) {
  return `/charts/top/${TopChartsType.sounds}/${timespan}/${sort}` as const;
}

export const defaultArtistsTopChartsPageSort = TopReleasesSortSubpaths['total-volume'];
export const defaultReleasesTopChartsPageSort = TopArtistsSortSubpaths['total-volume'];
export const defaultCollectorsTopChartsPageSort = TopCollectorsSortSubpaths['total-spent'];
export const defaultCuratorsTopChartsPageSort = TopCuratorsSortSubpaths['total-volume'];

export const DEFAULT_TOP_CHARTS_PATHNAME = ReleasesTopChartsPathname({
  timespan: defaultTimespan,
  sort: defaultReleasesTopChartsPageSort,
});

export function NavigateReleasesTopChartsPage(args: {
  timespan: Timespan;
  sort: TopReleasesSortSubpaths;
}) {
  return Router.push(ReleasesTopChartsPathname(args));
}

export function PrefetchReleasesTopChartsPage(args: {
  timespan: Timespan;
  sort: TopReleasesSortSubpaths;
}) {
  return Router.prefetch(ReleasesTopChartsPathname(args)).catch(err => {
    captureException(err, {
      level: 'warning',
    });
  });
}

export function ArtistsTopChartsPathname({
  timespan,
  sort,
}: {
  timespan: Timespan;
  sort: TopArtistsSortSubpaths;
}) {
  return `/charts/top/${TopChartsType.artists}/${timespan}/${sort}` as const;
}

export function NavigateArtistsTopChartsPage(args: {
  timespan: Timespan;
  sort: TopArtistsSortSubpaths;
}) {
  return Router.push(ArtistsTopChartsPathname(args)).catch(err => {
    captureException(err, {
      level: 'error',
    });
  });
}

export function PrefetchArtistsTopChartsPage(args: {
  timespan: Timespan;
  sort: TopArtistsSortSubpaths;
}) {
  return Router.prefetch(ArtistsTopChartsPathname(args)).catch(err => {
    captureException(err, {
      level: 'warning',
    });
  });
}

export function CollectorsTopChartsPathname({
  timespan,
  sort,
}: {
  timespan: Timespan;
  sort: TopCollectorsSortSubpaths;
}) {
  return `/charts/top/${TopChartsType.collectors}/${timespan}/${sort}` as const;
}

export function NavigateCollectorsTopChartsPage(args: {
  timespan: Timespan;
  sort: TopCollectorsSortSubpaths;
}) {
  return Router.push(CollectorsTopChartsPathname(args));
}

export function PrefetchCollectorsTopChartsPage(args: {
  timespan: Timespan;
  sort: TopCollectorsSortSubpaths;
}) {
  return Router.prefetch(CollectorsTopChartsPathname(args)).catch(err => {
    captureException(err, {
      level: 'warning',
    });
  });
}

export function CuratorsTopChartsPathname({
  timespan,
  sort,
}: {
  timespan: Timespan;
  sort: TopCuratorsSortSubpaths;
}) {
  return `/charts/top/${TopChartsType.curators}/${timespan}/${sort}` as const;
}

export function NavigateCuratorsTopChartsPage(args: {
  timespan: Timespan;
  sort: TopCuratorsSortSubpaths;
}) {
  return Router.push(CuratorsTopChartsPathname(args));
}

export function PrefetchCuratorsTopChartsPage(args: {
  timespan: Timespan;
  sort: TopCuratorsSortSubpaths;
}) {
  return Router.prefetch(CuratorsTopChartsPathname(args)).catch(err => {
    captureException(err, {
      level: 'warning',
    });
  });
}

export function HandleOnChartTypeSelection() {
  return function onChartTypeSelection(type: ChartTabsType) {
    switch (type) {
      case 'viral': {
        Router.push(NAV_LINKS.ViralChartsSounds);

        break;
      }
      default: {
        break;
      }
    }
  };
}

export function HandleOnChartContentSelection({ timespan }: { timespan: Timespan }) {
  return function onChartContentSelection(type: TopChartsType) {
    switch (type) {
      case 'sounds': {
        NavigateReleasesTopChartsPage({
          timespan,
          sort: defaultReleasesTopChartsPageSort,
        });
        return;
      }

      case 'artists': {
        NavigateArtistsTopChartsPage({
          timespan,
          sort: defaultArtistsTopChartsPageSort,
        });
        return;
      }

      case 'collectors': {
        NavigateCollectorsTopChartsPage({
          timespan,
          sort: defaultCollectorsTopChartsPageSort,
        });
        return;
      }

      case 'curators': {
        NavigateCuratorsTopChartsPage({
          timespan,
          sort: defaultCuratorsTopChartsPageSort,
        });
        return;
      }

      default: {
        exhaustiveGuard(type);
      }
    }
  };
}
